/* eslint-disable @typescript-eslint/naming-convention */
import Joi from '@hapi/joi';
import React from 'react';
import { toast } from 'react-toastify';
import { conditionCodeToName } from '../../../../../common/src/condition/conditionCodeToName';
import { CraConditionScores } from '../../../../../common/src/enums/CraConditionScores';
import { SurveyStates } from '../../../../../common/src/enums/SurveyStates';
import { customInspectionTypeFrontEndSettings } from '../../../customInspectionTypes/customInspectionTypeFrontEndSettings';
import { IWithReactRouterProps, withReactRouter } from '../../../mixins/router/withReactRouter';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchGet, fetchPost } from '../../../store/fetch';
import { openApiFetchGet } from '../../../store/openApiFetch';
import { InspectionStandardTypes } from '../../../utils/enums/InspectionStandardTypes';
import { Roles } from '../../../utils/enums/Roles';
import { isCustomerVersion } from '../../../utils/isCustomerVersion';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelFilterCreateSelect,
    EditPanelRow,
    EditPanelSection,
    PanelBody,
    PanelHeader,
    PopupElement,
    Tray,
} from '../../elements';
import { EquipmentUsage } from '../../Equipment/Common/EquipmentUsage';
import { FlightLogs } from '../../FlightLog/FlightLog';
import { Equipment, UsedEquipmentDto } from '../../FlightLog/interfaces';
import { Component } from '../../utils/Component';
import { ConditionScoreLabel } from '../../utils/ConditionScoreLabel';
import RoleDependent from '../../utils/RoleDependent';
import { showSavedToast } from '../../utils/toastMessages';
import { ValidatingFormMarkdownEditor } from '../../validatingComponents/markdown/ValidatingFormMarkdown';
import {
    ValidatingEditPanelSaveButton,
    ValidatingEditPanelSwitch,
    ValidatingFilterDropdownWithUrl,
    ValidatingForm,
    ValidatingFormContext,
    ValidatingFormInputDate,
    ValidatingFormInputText,
    ValidatingFormTextArea,
} from '../../validatingElements';
import { CraCondition } from '../CRA/CraCondition';
import { RiskScoreDropDown } from '../CRA/SurveyEditCraAnalysis';
import { SurveyEditGenerateReport } from './SurveyEditGenerateReport';
import { SurveyEditSurveyCommentsSection } from './SurveyEditSurveyCommentsSection';

const schema = Joi.object()
    .keys({
        craInitialHistoryScore: Joi.number().allow(null, ''),
        craInitialHistoryRemarks: Joi.string().when('craInitialHistoryScore', {
            is: Joi.number(),
            then: Joi.required(),
            otherwise: Joi.allow(null, ''),
        }),
        craInspectionScore: Joi.number().allow(null, ''),
        craInspectionRemarks: Joi.string().when('craInspectionScore', { is: Joi.number(), then: Joi.required(), otherwise: Joi.allow(null, '') }),
    })
    .unknown();

interface ISurveyEditSummaryAndAdviceEditProps extends ITranslateProps, IWithReactRouterProps {
    surveyId: string;
    objectId: string;
    status: string;
    isDefaultCompleted: boolean;
    isSurveyConditionCalculated: boolean;
    isConditionAvailable: boolean;
    isCraAvailable: boolean;
    isFmecaAvailable: boolean;
    labels: string[];
    surveyInspectionStandardType: InspectionStandardTypes;
}

interface ISurveyEditSummaryAndAdviceEditState {
    isLoading: boolean;
    isUploaded: boolean;
    showCompleteSurveyPopUp: boolean;
    equipment: UsedEquipmentDto[];
    isEquipmentLoading: boolean;
    equipmentWithLogs: Equipment[];
    knownLabels: string[];

    survey?: {
        objectId: string;
        objectName: string;
        condition?: string;
        careCondition?: string;
        craInitialHistoryCondition?: CraConditionScores;
        craInspectionCondition?: CraConditionScores;
        craInitialHistoryConditionWithoutFactor?: CraConditionScores;
        craInspectionConditionWithoutFactor?: CraConditionScores;
        craInitialHistoryConditionScoreWithoutFactor: number | null;
        craInspectionConditionScoreWithoutFactor: number | null;
        craInitialHistoryConditionScore: number | null;
        craInspectionConditionScore: number | null;
        inspectionStandardType: string;
        clientCompanyId: string;
        surveyorCompanyId: string;
        operatorCompanyId: string;
        ownerName: string;
        clientName: string;
        surveryedOn: Date;
        reportDeadlineOnDate: string | null;
        canReadComments: boolean;
    };
}

class SurveyEditSummaryAndAdvice extends Component<ISurveyEditSummaryAndAdviceEditProps, ISurveyEditSummaryAndAdviceEditState> {
    constructor(props: ISurveyEditSummaryAndAdviceEditProps) {
        super(props);

        this.state = {
            equipment: [],
            equipmentWithLogs: [],
            isEquipmentLoading: false,
            isLoading: false,
            isUploaded: true,
            knownLabels: [],
            showCompleteSurveyPopUp: false,
        };
    }

    public override async componentDidMount() {
        await this.load();

        // we do not need those right away
        setTimeout(async () => {
            await this.loadKnownLabels();

            if (isCustomerVersion('ronik')) {
                await this.loadEquipmentList();
            }
        }, 500);
    }

    public override render() {
        if (!this.state.survey) {
            return null;
        }

        // const isFmecaAndCra = this.props.isCraAvailable && this.props.isFmecaAvailable;

        const InspectionTypeComponent = this.getInspectionTypeComponent();

        return (
            <Tray>
                <InspectionTypeComponent surveyId={this.props.surveyId} />
                <ValidatingForm
                    schema={schema}
                    itemUrl={`/api/surveys/${this.props.surveyId}`}
                    id={this.props.surveyId}
                    onSubmitted={async () => await this.handleOnSubmitted()}
                >
                    {this.props.isCraAvailable && (
                        <EditPanel bottomMargin={true} noPadding={true}>
                            <CollapsiblePanel heading={{ dark: true, text: this.props.t('survey.isCraAvailable'), initialIsClosed: true }}>
                                <PanelBody isForm={true}>
                                    <PanelHeader text={this.props.t('survey.craInitialHistory')} />
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={1}>
                                            {' '}
                                            <RiskScoreDropDown name="craInitialHistoryScore" postText="(EJ)" />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={11}>
                                            <ValidatingFormTextArea
                                                size="xs"
                                                name="craInitialHistoryRemarks"
                                                placeholder={this.props.t('common.remarks')}
                                            />{' '}
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>
                                            <ValidatingEditPanelSwitch
                                                name="craInitialHistoryIsBuildBetween4074"
                                                placeholder={this.props.t('survey.craIsBuildBetween4074')}
                                            />{' '}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={8}>
                                            <ValidatingFormTextArea
                                                size="xs"
                                                name="craInitialHistoryBuildBetween4074Remarks"
                                                placeholder={this.props.t('common.remarks')}
                                            />{' '}
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>
                                            <ValidatingEditPanelSwitch
                                                name="craInitialHistoryIsStaticallyIndeterminate"
                                                placeholder={this.props.t('survey.craIsStaticallyIndeterminate')}
                                            />{' '}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={8}>
                                            <ValidatingFormTextArea
                                                size="xs"
                                                name="craInitialHistoryStaticallyIndeterminateRemarks"
                                                placeholder={this.props.t('common.remarks')}
                                            />{' '}
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <PanelHeader text={this.props.t('survey.craInspection')} />
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={1}>
                                            {' '}
                                            <RiskScoreDropDown name="craInspectionScore" postText="(EJ)" />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={11}>
                                            <ValidatingFormTextArea
                                                size="xs"
                                                name="craInspectionRemarks"
                                                placeholder={this.props.t('common.remarks')}
                                            />{' '}
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>
                                            <ValidatingEditPanelSwitch
                                                name="craInspectionIsBuildBetween4074"
                                                placeholder={this.props.t('survey.craIsBuildBetween4074')}
                                            />{' '}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={8}>
                                            <ValidatingFormTextArea
                                                size="xs"
                                                name="craInspectionBuildBetween4074Remarks"
                                                placeholder={this.props.t('common.remarks')}
                                            />{' '}
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>
                                            <ValidatingEditPanelSwitch
                                                name="craInspectionIsStaticallyIndeterminate"
                                                placeholder={this.props.t('survey.craIsStaticallyIndeterminate')}
                                            />{' '}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={8}>
                                            <ValidatingFormTextArea
                                                size="xs"
                                                name="craInspectionStaticallyIndeterminateRemarks"
                                                placeholder={this.props.t('common.remarks')}
                                            />{' '}
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelButtons>
                                        <ValidatingEditPanelSaveButton small={true} />
                                    </EditPanelButtons>
                                </PanelBody>
                            </CollapsiblePanel>
                        </EditPanel>
                    )}

                    {this.props.isCraAvailable && (
                        <EditPanel bottomMargin={true} noPadding={true}>
                            <CollapsiblePanel heading={{ dark: true, text: this.props.t('survey.combinedRiskScores'), initialIsClosed: true }}>
                                <PanelBody isForm={true}>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>&nbsp;</EditPanelCell>
                                        <EditPanelCell rowCount={3}>
                                            <strong>Inclusief EJ</strong>
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={3}>
                                            <strong>Exclusief EJ</strong>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>{this.props.t('survey.craInitialHistoryCondition')}</EditPanelCell>
                                        <EditPanelCell rowCount={3}>
                                            <CraCondition conditionScore={this.state.survey.craInitialHistoryCondition} />{' '}
                                            {this.state.survey.craInitialHistoryConditionScore && (
                                                <>({this.state.survey.craInitialHistoryConditionScore})</>
                                            )}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={3}>
                                            <CraCondition conditionScore={this.state.survey.craInitialHistoryConditionWithoutFactor} />{' '}
                                            {this.state.survey.craInitialHistoryConditionScoreWithoutFactor && (
                                                <>({this.state.survey.craInitialHistoryConditionScoreWithoutFactor})</>
                                            )}
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>{this.props.t('survey.craInspectionCondition')}</EditPanelCell>
                                        <EditPanelCell rowCount={3}>
                                            <CraCondition conditionScore={this.state.survey.craInspectionCondition} />{' '}
                                            {this.state.survey.craInspectionConditionScore && <>({this.state.survey.craInspectionConditionScore})</>}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={3}>
                                            <CraCondition conditionScore={this.state.survey.craInspectionConditionWithoutFactor} />{' '}
                                            {this.state.survey.craInspectionConditionScoreWithoutFactor && (
                                                <>({this.state.survey.craInspectionConditionScoreWithoutFactor})</>
                                            )}
                                        </EditPanelCell>
                                    </EditPanelRow>
                                </PanelBody>
                            </CollapsiblePanel>
                        </EditPanel>
                    )}

                    {[
                        InspectionStandardTypes.Api653,
                        InspectionStandardTypes.Api570,
                        InspectionStandardTypes.Eemua159,
                        InspectionStandardTypes.Eemua159,
                        InspectionStandardTypes.Fmeca,
                        InspectionStandardTypes.Free,
                        InspectionStandardTypes.Memo,
                        InspectionStandardTypes.Model3d,
                        InspectionStandardTypes.Nen2767,
                    ].includes(this.state.survey.inspectionStandardType as InspectionStandardTypes) &&
                        (this.props.isFmecaAvailable || !this.props.isCraAvailable) && (
                            <EditPanel bottomMargin={true} noPadding={true}>
                                <CollapsiblePanel
                                    heading={{
                                        dark: true,
                                        text: this.props.isFmecaAvailable
                                            ? `${this.props.t('survey.summaryAndAdvice')} FMECA`
                                            : this.props.t('survey.summaryAndAdvice'),
                                        initialIsClosed: this.state.survey.inspectionStandardType !== InspectionStandardTypes.Memo,
                                    }}
                                >
                                    <PanelBody isForm={true}>
                                        <EditPanelSection>
                                            <ValidatingFormMarkdownEditor
                                                name="summaryAndAdvice"
                                                placeholder={this.props.t('survey.composeSummaryAndAdvice')}
                                            />
                                        </EditPanelSection>

                                        {this.props.isConditionAvailable && this.props.isSurveyConditionCalculated && (
                                            <>
                                                <PanelHeader text={this.props.t('survey.conditionScore')} />
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <div className="mr10">
                                                            {this.props.t('survey.compositeCondition')}:{' '}
                                                            <ConditionScoreLabel value={this.state.survey.condition} />
                                                        </div>
                                                        <div className="mr10 mt20">
                                                            {this.props.t('survey.compositeCare')}:{' '}
                                                            <ConditionScoreLabel value={this.state.survey.careCondition} />
                                                        </div>
                                                    </EditPanelCell>
                                                    <EditPanelCell rowCount={6}>
                                                        <EditPanelButton
                                                            disabled={this.state.isLoading}
                                                            onClick={async () => this.recalculate()}
                                                            text={this.props.t('survey.recalculateDerivedCondition')}
                                                            iconClass="fa-gears"
                                                        />
                                                        <RoleDependent
                                                            allowed={[Roles.Administrator]}
                                                            content={
                                                                <EditPanelButton
                                                                    disabled={this.state.isLoading}
                                                                    onClick={async () => this.resetAndCalculateConditions()}
                                                                    text={this.props.t('survey.resetConditions')}
                                                                    iconClass="fa-gears"
                                                                />
                                                            }
                                                        />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                            </>
                                        )}
                                        {this.props.isConditionAvailable && !this.props.isSurveyConditionCalculated && (
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={4}>
                                                    <ValidatingFormContext.Consumer>
                                                        {(state) => (
                                                            <>
                                                                <span className="ml5">{this.props.t('common.result')}</span>
                                                                <label className="field select">
                                                                    <select
                                                                        id="condition-select"
                                                                        name="condition "
                                                                        className={`label-${conditionCodeToName(this.state.survey?.condition)}`}
                                                                        style={{ color: '#fff' }}
                                                                        value={state.item.condition || ''}
                                                                        onChange={async (e) => state.onChange('condition', e.target.value)}
                                                                    >
                                                                        <option style={{ color: '#000' }} value="">
                                                                            {this.props.t('common.selectItem', this.props.t('common.condition'))}
                                                                        </option>
                                                                        <option style={{ color: '#000' }} value="1">
                                                                            {this.props.t('condition.excellent')}
                                                                        </option>
                                                                        <option style={{ color: '#000' }} value="2">
                                                                            {this.props.t('condition.good')}
                                                                        </option>
                                                                        <option style={{ color: '#000' }} value="3">
                                                                            {this.props.t('condition.reasonable')}
                                                                        </option>
                                                                        <option style={{ color: '#000' }} value="4">
                                                                            {this.props.t('condition.moderate')}
                                                                        </option>
                                                                        <option style={{ color: '#000' }} value="5">
                                                                            {this.props.t('condition.poor')}
                                                                        </option>
                                                                        <option style={{ color: '#000' }} value="6">
                                                                            {this.props.t('condition.bad')}
                                                                        </option>
                                                                        <option style={{ color: '#000' }} value="U">
                                                                            {this.props.t('condition.unknown')}
                                                                        </option>
                                                                        <option style={{ color: '#000' }} value="F">
                                                                            {this.props.t('survey.furtherInvestigation')}
                                                                        </option>
                                                                    </select>
                                                                    <i className="arrow" />
                                                                </label>
                                                            </>
                                                        )}
                                                    </ValidatingFormContext.Consumer>
                                                </EditPanelCell>
                                            </EditPanelRow>
                                        )}
                                        <EditPanelButtons>
                                            <ValidatingEditPanelSaveButton small={true} />
                                        </EditPanelButtons>
                                    </PanelBody>
                                </CollapsiblePanel>
                            </EditPanel>
                        )}

                    {this.state.survey.canReadComments && <SurveyEditSurveyCommentsSection surveyId={this.props.surveyId} />}

                    {/* Settings */}
                    <EditPanel bottomMargin={true} noPadding={true}>
                        <CollapsiblePanel heading={{ dark: true, text: this.props.t('common.settings'), initialIsClosed: true }}>
                            <PanelBody isForm={true}>
                                <EditPanelSection>
                                    <ValidatingFormInputText name="description" placeholder={this.props.t('survey.description')} icon="fa fa-tag" />
                                </EditPanelSection>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormContext.Consumer>
                                            {(state) => (
                                                <>
                                                    <span className="ml5">{this.props.t('common.status')}</span>
                                                    <label className="field select mt5">
                                                        <select
                                                            id="status"
                                                            name="status"
                                                            value={state.item.status || ''}
                                                            onChange={async (e) => state.onChange('status', e.target.value)}
                                                        >
                                                            <option value={SurveyStates.Open}>
                                                                {this.props.t(`surveyStatus.${SurveyStates.Open}`)}
                                                            </option>
                                                            <option value={SurveyStates.Preparation}>
                                                                {this.props.t(`surveyStatus.${SurveyStates.Preparation}`)}
                                                            </option>
                                                            <option value={SurveyStates.Conducting}>
                                                                {this.props.t(`surveyStatus.${SurveyStates.Conducting}`)}
                                                            </option>
                                                            <option value={SurveyStates.Reporting}>
                                                                {this.props.t(`surveyStatus.${SurveyStates.Reporting}`)}
                                                            </option>
                                                            <option value={SurveyStates.Delivery}>
                                                                {this.props.t(`surveyStatus.${SurveyStates.Delivery}`)}
                                                            </option>
                                                            <RoleDependent
                                                                allowed={[Roles.Administrator, Roles.Owner, Roles.Client]}
                                                                content={
                                                                    <option value={SurveyStates.Completed}>
                                                                        {this.props.t(`surveyStatus.${SurveyStates.Completed}`)}
                                                                    </option>
                                                                }
                                                            />
                                                            <RoleDependent
                                                                allowed={[Roles.Administrator]}
                                                                content={
                                                                    <option value={SurveyStates.Deleted}>
                                                                        {this.props.t(`surveyStatus.${SurveyStates.Deleted}`)}
                                                                    </option>
                                                                }
                                                            />
                                                        </select>
                                                        <i className="arrow"></i>
                                                    </label>
                                                </>
                                            )}
                                        </ValidatingFormContext.Consumer>
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormInputText name="referenceNumber" topLabel={this.props.t('survey.referenceNumber')} />
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormInputText name="revisionNumber" topLabel={this.props.t('survey.revisionNumber')} />
                                    </EditPanelCell>
                                </EditPanelRow>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={12}>
                                        <ValidatingFormContext.Consumer>
                                            {(state) => (
                                                <>
                                                    <span className="ml5">{this.props.t('common.labels')}</span>
                                                    <label className="field select mt5">
                                                        <EditPanelFilterCreateSelect
                                                            name="labels"
                                                            placeholder={this.props.t('common.selectItem', this.props.t('common.labels'))}
                                                            value={state.item.labels}
                                                            options={this.state.knownLabels}
                                                            handleChange={async (value: string[], name: string) => state.onChange(name, value)}
                                                        />
                                                    </label>
                                                </>
                                            )}
                                        </ValidatingFormContext.Consumer>
                                    </EditPanelCell>
                                </EditPanelRow>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormInputDate name="surveryedOn" topLabel={this.props.t('survey.surveyedOn')} />
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormInputDate name="reportDeadlineOnDate" topLabel={this.props.t('survey.reportDeadlineOnDate')} />
                                    </EditPanelCell>
                                </EditPanelRow>

                                <PanelHeader text={this.props.t('asset.settings')} />
                                <EditPanelSection>
                                    <ValidatingEditPanelSwitch
                                        name="isVideoDownloadVisible"
                                        placeholder={this.props.t('survey.isVideoDownloadVisible')}
                                    />

                                    <button type="button" className="btn btn-sm mt10 fs12 show" onClick={() => this.updateAssetsInReport(true)}>
                                        {this.props.t('survey.selectAllImages')}
                                    </button>

                                    <button type="button" className="btn btn-sm mt10 fs12 show" onClick={() => this.updateAssetsInReport(false)}>
                                        {this.props.t('survey.deselectAllImages')}
                                    </button>
                                </EditPanelSection>
                                <EditPanelButtons>
                                    <ValidatingEditPanelSaveButton small={true} />
                                </EditPanelButtons>
                            </PanelBody>
                        </CollapsiblePanel>
                    </EditPanel>

                    {isCustomerVersion('ronik') && (
                        <EditPanel bottomMargin={true} noPadding={true}>
                            <CollapsiblePanel heading={{ dark: true, text: this.props.t('equipment.usedEquipment'), initialIsClosed: true }}>
                                <EquipmentUsage
                                    data={this.state.equipment}
                                    isLoading={this.state.isEquipmentLoading}
                                    reload={async () => await this.loadEquipmentList()}
                                    surveyId={this.props.surveyId}
                                />
                            </CollapsiblePanel>
                        </EditPanel>
                    )}

                    {isCustomerVersion('ronik') && (
                        <EditPanel bottomMargin={true} noPadding={true}>
                            <CollapsiblePanel
                                heading={{
                                    dark: true,
                                    text: this.props.t('flightLog.log'),
                                    initialIsClosed: true,
                                }}
                            >
                                {this.state.equipmentWithLogs.length > 0 && (
                                    <FlightLogs
                                        equipment={this.state.equipmentWithLogs}
                                        surveyId={this.props.surveyId}
                                        surveyedOn={this.state.survey.surveryedOn}
                                    />
                                )}

                                {this.state.equipmentWithLogs.length === 0 && <p className="p20">{this.props.t('flightLog.attachDeviceFirst')}</p>}
                            </CollapsiblePanel>
                        </EditPanel>
                    )}

                    {/* Signed */}
                    {this.state.survey.inspectionStandardType !== InspectionStandardTypes.Memo && (
                        <EditPanel bottomMargin={true} noPadding={true}>
                            <CollapsiblePanel heading={{ dark: true, text: `${this.props.t('survey.closeSurvey')}`, initialIsClosed: true }}>
                                <PanelBody isForm={true}>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={5}>
                                            <strong>{this.props.t('survey.preparedBy')}</strong>
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={2}>&nbsp;</EditPanelCell>
                                        <EditPanelCell rowCount={5}>
                                            <strong>{this.props.t('survey.verifiedBy')}</strong>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={5}>
                                            <ValidatingFormInputText name="preparedAuthor" placeholder={this.props.t('common.name')} />{' '}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={2}>&nbsp;</EditPanelCell>
                                        <EditPanelCell rowCount={5}>
                                            <ValidatingFormInputText name="verifiedAuthor" placeholder={this.props.t('common.name')} />{' '}
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={5}>
                                            <ValidatingFormInputDate name="preparedDate" placeholder={this.props.t('common.date')} />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={2}>&nbsp;</EditPanelCell>
                                        <EditPanelCell rowCount={5}>
                                            <ValidatingFormInputDate name="verifiedDate" placeholder={this.props.t('common.date')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelButtons>
                                        {this.props.status !== SurveyStates.Completed && (
                                            <EditPanelButton
                                                text={this.props.t('survey.completeSurvey')}
                                                small={true}
                                                onClick={async () => await this.setState({ showCompleteSurveyPopUp: true })}
                                            />
                                        )}
                                        <ValidatingEditPanelSaveButton small={true} />
                                    </EditPanelButtons>
                                </PanelBody>
                            </CollapsiblePanel>
                        </EditPanel>
                    )}

                    {/* Admin/Client authorization settings */}
                    <RoleDependent
                        allowed={[Roles.Administrator, Roles.Client]}
                        content={
                            <EditPanel bottomMargin={true} noPadding={true}>
                                <CollapsiblePanel heading={{ dark: true, text: `${this.props.t('object.adminSettings')}`, initialIsClosed: true }}>
                                    <PanelBody isForm={true}>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={4}>
                                                <strong>{this.props.t('common.client')}</strong>: {this.state.survey.clientName}
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={4}>
                                                <strong>{this.props.t('common.owner')}</strong>: {this.state.survey.ownerName}
                                            </EditPanelCell>
                                        </EditPanelRow>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <ValidatingFilterDropdownWithUrl
                                                    url={`/api/companyList?type=surveyor&companyid=${this.state.survey.clientCompanyId}`}
                                                    name="surveyorCompanyId"
                                                    topLabel={this.props.t('common.surveyor')}
                                                />
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={6}>
                                                <ValidatingFilterDropdownWithUrl
                                                    url={`/api/companyList?type=operator&companyid=${this.state.survey.clientCompanyId}`}
                                                    name="operatorCompanyId"
                                                    topLabel={this.props.t('common.operator')}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                        {this.state.survey.inspectionStandardType === InspectionStandardTypes.Fmeca && (
                                            <>
                                                <PanelHeader
                                                    text={`${this.props.t('common.settings')} ${this.props.t('inspectionStandard.fmeca')}`}
                                                />
                                                <EditPanelSection>
                                                    <ValidatingEditPanelSwitch
                                                        name="isFmecaAvailable"
                                                        placeholder={this.props.t('survey.isFmecaAvailable')}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelSection>
                                                    <ValidatingEditPanelSwitch
                                                        name="isCraAvailable"
                                                        placeholder={this.props.t('survey.isCraAvailable')}
                                                    />
                                                </EditPanelSection>
                                            </>
                                        )}
                                        <EditPanelButtons>
                                            <ValidatingEditPanelSaveButton small={true} />
                                        </EditPanelButtons>
                                    </PanelBody>
                                </CollapsiblePanel>
                            </EditPanel>
                        }
                    />

                    {this.state.showCompleteSurveyPopUp && (
                        <PopupElement small={true} onClick={async () => await this.setState({ showCompleteSurveyPopUp: false })}>
                            <div className="panel-body bg-light darker">
                                <EditPanelSection>
                                    <p>{this.props.t('survey.closeSurveyExplanation')}</p>
                                </EditPanelSection>
                                <EditPanelButtons>
                                    <EditPanelButton
                                        onClick={async () => await this.setState({ showCompleteSurveyPopUp: false })}
                                        small={true}
                                        text={this.props.t('common.cancel')}
                                        disabled={!this.state.isUploaded}
                                    />
                                    <EditPanelButton
                                        onClick={async () => await this.completeSurvey()}
                                        small={true}
                                        primary={true}
                                        text={this.props.t('survey.completeSurvey')}
                                        disabled={!this.state.isUploaded}
                                    />
                                </EditPanelButtons>
                            </div>
                        </PopupElement>
                    )}
                </ValidatingForm>

                <SurveyEditGenerateReport surveyId={this.props.surveyId} surveyInspectionStandardType={this.props.surveyInspectionStandardType} />
            </Tray>
        );
    }

    private async completeSurvey() {
        await this.setState({ isUploaded: false });
        await fetchPost(`/api/surveys/${this.props.surveyId}`, undefined, { status: SurveyStates.Completed });
        this.props.navigate(`/surveys/${this.props.surveyId}`);
    }

    private async handleOnSubmitted() {
        showSavedToast(this.props.t);
        await this.load();
    }

    private async load() {
        const url = `/api/surveys/${this.props.surveyId}`;
        const result = await fetchGet(url);
        result.json.surveryedOn = new Date(result.json.surveryedOn);
        await this.setState({ survey: result.json });
    }

    private async loadEquipmentList() {
        const url = '/api/equipmentUsage';
        const params = { query: { surveyId: this.props.surveyId } };
        await this.setState({ isEquipmentLoading: true });
        const result = await openApiFetchGet(url, params.query);
        const equipment = (result ?? []) as any[];
        const equipmentWithLogs = this.prepareListOfSlimEquip(equipment);
        await this.setState({ equipment, equipmentWithLogs, isEquipmentLoading: false });
    }

    private async recalculate() {
        const url = `/api/surveys/${this.props.surveyId}/recalculateDerivedCondition`;
        await this.setState({ isLoading: true });
        await fetchPost(url, {});
        await this.load();
        showSavedToast(this.props.t);
        await this.setState({ isLoading: false });
    }

    private getInspectionTypeComponent(): (props: { surveyId: string }) => JSX.Element | null {
        if (!this.state.survey) {
            return () => null;
        }
        if (customInspectionTypeFrontEndSettings[this.state.survey.inspectionStandardType]) {
            // eslint-disable-next-line @typescript-eslint/unbound-method
            return customInspectionTypeFrontEndSettings[this.state.survey.inspectionStandardType]!.getSurveyEditSummaryAndAdvice;
        }
        return () => null;
    }

    private async resetAndCalculateConditions() {
        await this.setState({ isLoading: true });
        await fetchPost(`/api/surveys/${this.props.surveyId}/resetAndCaculateConditons`, {});
        await this.load();
        showSavedToast(this.props.t);
        await this.setState({ isLoading: false });
    }

    private prepareListOfSlimEquip(equipment: UsedEquipmentDto[]): Equipment[] {
        return equipment
            .filter((item) => item.equipment.equipmentType.hasLog)
            .map((item) => ({
                id: item.equipment.id,
                name: item.equipment.name,
            }));
    }

    private async loadKnownLabels() {
        const url = '/api/surveys/knownLabels';
        const result = await fetchGet(url);
        await this.setState({ knownLabels: result?.json });
    }

    private updateAssetsInReport(select: boolean) {
        fetchPost(`/api/surveys/${this.props.surveyId}/updateAssetsInReport`, {}, { select })
            .then(() => {
                toast.info(this.props.t(select ? 'survey.allImagesSelected' : 'survey.allImagesDeselected'), {
                    autoClose: 3000,
                    position: 'top-center' as any,
                });
            })
            .catch((err) => {
                console.error(err);
                toast.error(this.props.t('survey.allImagesSelectFailed'), { autoClose: 3000, position: 'top-center' as any });
            });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(withReactRouter(SurveyEditSummaryAndAdvice));
